import { Suspense, useEffect, useMemo } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
// routes
import { HIDE_SIDEBAR_ROUTES } from "constants/proRoutes";
import { ROUTES, SIDEBAR_ROUTES } from "./routes";
// hooks
import { useUser } from "contexts/UserContext";
import { useAuthModals } from "contexts/auth-modals-context";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
// contexts
import DoctorDashboardPageContextProvider from "./contexts/index";
import canPrintMultiPage from "../utility/canPrintMultiPage";
// components
import Loading from "components/Loading";
import InvoicingEditorPdf from "components/invoicing/InvoicingEditorPdf";
import PageNotFounded from "components/page-not-found";
import ReceiptViewer from "components/receipt-viewer";
import SuperbillViewer from "components/superbill-viewer/SuperbillViewer";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import { useSubscription } from "contexts/subscription-context";
import Print from "./notes-and-forms/scenes/print/Print";
// constants
import AINavbar from "components/ai-navbar/AINavbar";
import MFA from "components/mfa/MFA";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import lazy from "utils/lazyOrReload";
import AudioRecorder from "./audio-recorder/AudioRecorder";

import styles from "./DoctorDashboard.module.scss";
import getClasses from "utils/getClasses";
import postOrchidPro from "services/postOrchidPro";

const Settings = lazy(() => import("./settings"));
const ChangeSubscriptionPlan = lazy(
  () => import("./subscription-setting/change-plan/ChangePlan"),
);
const CancelPage = lazy(
  () => import("./subscription-setting/cancel-page/CancelPage"),
);

const UpdatePayment = lazy(
  () => import("./subscription-setting/update-payment/UpdatePayment"),
);
const Subclass = lazy(
  () => import("./subscription-setting/change-plan/subclass"),
);

const AfterLoginNavbar = lazy(
  () => import("components/after-login-navbar/AfterLoginNavbar"),
);
const RedirectAfterStripeConnect = lazy(
  () => import("./components/stripe-setup-button/after-stripe-connect"),
);
const GroupSession = lazy(() => import("./group-sessions/GroupSession"));
const GroupSessionCreateOrEdit = lazy(
  () => import("./group-sessions/GroupSessionCreateOrEdit"),
);
const GroupSessionSpeakerConfirm = lazy(
  () => import("./group-sessions/speaker-confirm"),
);

const ConfirmAllAttendance = lazy(
  () => import("./confirm-all-sessions/ConfirmAllAttendance"),
);
const ApproveClientSignup = lazy(() => import("./approve-client-signup"));
const ClientPortalRequestApproved = lazy(
  () => import("./approve-client-signup/request-approved"),
);

const Messages = lazy(() => import("../messages/Messages"));
const NoteFillIn = lazy(
  () => import("./notes-and-forms/scenes/note-fill-in/NoteFillIn"),
);
const FormFillIn = lazy(
  () => import("./notes-and-forms/scenes/form-fill-in/FormFillIn"),
);

const PatientChart = lazy(
  () => import("./patient-charts/components/patient-chart/PatientChart"),
);
const OverridePatient = lazy(() => import("components/auth/override-profile"));
const PostBlog = lazy(() => import("./post-blog"));
const NavSidebar = lazy(() => import("./nav-sidebar/NavSidebar"));
const AddClientPayment = lazy(
  () => import("./add-client-payment/AddClientPayment"),
);
const InvoicingCheckoutConfirm = lazy(
  () => import("./add-client-payment/checkout-confirm"),
);

const AIHome = lazy(() => import("components/ai-home/AIHome"));
const AIPatientChart = lazy(
  () => import("components/ai-patient-chart/AIPatientChart"),
);

const AISessionReviewWithContext = lazy(
  () => import("components/ai-session-review/AISessionReviewWithContext"),
);

const NewTemplateEditor = lazy(
  () => import("notes-and-forms-v2/components/template-editor/TemplateEditor"),
);

const DoctorDashboard = () => {
  const { user, updateUserInfo } = useUser();
  const { search, pathname } = useLocation();
  const { isPhonePortrait } = usePhonePortraitContext();
  const { isSubscribed, canAccessFeature, planType, canAccessPlatform } =
    useSubscription();
  const { canAccessFeatureByRole } = useTeamContext();

  useEffect(() => {
    if (search === "/after-stripe-connect") {
      updateUserInfo();
    }
  }, [search, updateUserInfo]);

  const getRoutes = (isSubscribed) =>
    ROUTES.map((prop, key) => {
      //requirement check: if true, show sidebar routes (i.e. dashboard home)
      const checkRequirement = [
        prop.bypassProfileApprovedToUsePlatform ||
          !!user?.profile_approved_to_use_the_platform,
        prop.requiresSubscription ? isSubscribed : true,
        prop.featureName ? canAccessFeature(prop.featureName) : true,
        prop.roleFeatureName
          ? canAccessFeatureByRole(prop.roleFeatureName)
          : true, // Role-based access control
      ].every((el) => el === true);

      if (checkRequirement) {
        return (
          <Route path={prop.path} element={<prop.component />} key={key} />
        );
      } else {
        return (
          <Route path={prop.path} element={<PageNotFounded />} key={key} />
        );
      }
    });

  const { openEmailSentModal } = useAuthModals();
  const navigate = useNavigate();

  useEffect(() => {
    const checkUser = async () => {
      if (user === null) return;

      if (user && !user.email_confirmed) {
        return openEmailSentModal();
      }

      if (user && user.email_confirmed && !user.signup_form_finished) {
        if (
          user?.user_type === "orchid_pro" &&
          user?.role === "PRACTICE_SCHEDULER"
        ) {
          try {
            await postOrchidPro({
              ...user,
              signature_legal_first_name: user.first_name,
              signature_legal_last_name: user.last_name,
              dob: "1990-01-01",
              confirmed_have_social_security_number_allowed_to_work_in_usa:
                true,
              agreed_to_terms_of_use: true,
            });
            navigate("/pro-dashboard/schedule");
            return;
          } catch (error) {
            console.error(error);
          }
        } else {
          return navigate("/quick-intake");
        }
      }

      if (
        user?.user_type === "orchid_pro" &&
        user?.role === "PRACTICE_SCHEDULER" &&
        // !canAccessFeatureByRole("dashboard") &&
        pathname === "/pro-dashboard/homepage"
      ) {
        return navigate("/pro-dashboard/schedule");
      }
    };

    checkUser();
  }, [user, openEmailSentModal, canAccessFeatureByRole, navigate, pathname]);

  /**
   * control whether to render the nav sidebar - only on base routes from ROUTES (exclude settings/*)
   */
  const shouldShowSidebar = useMemo(() => {
    if (!canAccessPlatform(user)) return false;
    if (pathname.startsWith("/pro-dashboard/add-client-payment")) return false;
    if (pathname.startsWith("/pro-dashboard/settings")) return true;
    const base = "/pro-dashboard";
    const sidebarPaths = ROUTES.filter(
      (r) => !r.path.startsWith("settings/"),
    ).map((r) => `${base}/${r.path}`);
    return sidebarPaths.some((p) => pathname === p);
  }, [pathname, user, canAccessPlatform]);

  const showSettings = useMemo(() => {
    return pathname.startsWith("/pro-dashboard/settings");
  }, [pathname]);

  const hideNavbar = useMemo(() => {
    const shouldHideNavbarOnAllDevices =
      pathname.startsWith("/pro-dashboard/schedule") ||
      pathname.startsWith("/pro-dashboard/patient-charts/") ||
      pathname.startsWith("/pro-dashboard/client/") ||
      // Note: old /invoice means receipt viewer
      pathname.startsWith("/pro-dashboard/invoice") ||
      pathname.startsWith("/pro-dashboard/invoicing") ||
      pathname.startsWith("/pro-dashboard/superbill") ||
      pathname.endsWith("/request-approved") ||
      pathname.startsWith("/pro-dashboard/add-client-payment") ||
      pathname.startsWith("/pro-dashboard/audio-recorder") ||
      pathname.startsWith("/pro-dashboard/session-review/") ||
      pathname.startsWith("/pro-dashboard/session-review-1/") ||
      pathname.startsWith("/pro-dashboard/notes-and-forms/edit-template");

    const shouldHideNavbarOnPhone = pathname.startsWith(
      "/pro-dashboard/messages",
    );
    return (
      shouldHideNavbarOnAllDevices ||
      (isPhonePortrait && shouldHideNavbarOnPhone)
    );
  }, [pathname, isPhonePortrait]);

  const showEmptyNavbar = useMemo(() => {
    return (
      pathname.startsWith("/pro-dashboard/notes-and-forms/note") ||
      pathname.startsWith(
        "/pro-dashboard/notes-and-forms/view-note-template",
      ) ||
      pathname.startsWith("/pro-dashboard/notes-and-forms/form") ||
      pathname.startsWith(
        "/pro-dashboard/notes-and-forms/view-form-template",
      ) ||
      pathname.startsWith("/pro-dashboard/old-notes-and-forms/edit-template") ||
      pathname.startsWith("/pro-dashboard/notes-and-forms/print") ||
      pathname.startsWith("/pro-dashboard/confirm-all-attendance")
    );
  }, [pathname]);

  const shouldUseAINavbar = useMemo(() => {
    return (
      planType === "ai-only" &&
      !pathname.startsWith("/pro-dashboard/audio-recorder") &&
      !pathname.startsWith("/pro-dashboard/session-review") &&
      !pathname.startsWith("/pro-dashboard/notes-and-forms/edit-template/note/")
    );
  }, [pathname, planType]);

  if (user?.user_type !== "orchid_pro") {
    return;
  }

  return (
    <DoctorDashboardPageContextProvider>
      <DndProvider backend={HTML5Backend}>
        <div
          className={getClasses(
            styles["pro-dashboard"],
            canPrintMultiPage(pathname) && styles["pro-dashboard--multi-page"],
            isPhonePortrait && styles["pro-dashboard--phone"],
          )}
        >
          {shouldUseAINavbar ? (
            <AINavbar />
          ) : hideNavbar ? null : showEmptyNavbar ? (
            <div className={styles["empty-header"]}></div>
          ) : pathname.startsWith(
              "/pro-dashboard/invoicing-checkout-confirm",
            ) ? (
            <div className={styles["empty-header--add-client-payment"]}></div>
          ) : (
            <AfterLoginNavbar />
          )}
          {user?.signup_form_finished && (
            <main
              className={getClasses(
                canPrintMultiPage(pathname)
                  ? styles["pro-dashboard__body--multi-page"]
                  : styles["pro-dashboard__body"],
                pathname.startsWith(
                  "/pro-dashboard/notes-and-forms/edit-template",
                ) && styles["pro-dashboard__body--edit-template"],
              )}
              draggable={false}
              onDragStart={() => false}
            >
              {!isPhonePortrait && shouldShowSidebar && (
                <NavSidebar
                  routes={SIDEBAR_ROUTES}
                  isSubscribed={isSubscribed}
                />
              )}
              {showSettings ? (
                <Suspense fallback={<Loading />}>
                  <Routes>
                    <Route path="settings/*" element={<Settings />} />
                  </Routes>
                </Suspense>
              ) : (
                <div
                  className={getClasses(
                    styles["main-panel"],
                    HIDE_SIDEBAR_ROUTES.some((el) => pathname.startsWith(el)) &&
                      styles["main-panel--sidebar-close"],
                    !shouldShowSidebar && styles["main-panel--full-width"],
                  )}
                >
                  <Suspense fallback={<Loading />}>
                    <Routes>
                      {/* ------------AI-Only User------------- */}
                      <Route
                        path="home"
                        element={
                          canAccessFeature("ai-home") ? (
                            <AIHome />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="client/:patient_user_id"
                        element={
                          canAccessFeature("ai-patient-chart") ? (
                            <AIPatientChart />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="session-review/:appointment_id"
                        element={
                          canAccessFeature("ai-session-review") ? (
                            <AISessionReviewWithContext />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />

                      {/* ------------Regular plan User------------- */}
                      {getRoutes(isSubscribed)}
                      <Route
                        path="after-stripe-connect"
                        element={<RedirectAfterStripeConnect />}
                      />
                      <Route
                        path="group-sessions/create/"
                        element={
                          canAccessFeature("group-sessions") ? (
                            <GroupSessionCreateOrEdit />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="group-sessions/speaker-confirm"
                        element={
                          canAccessFeature("group-sessions") ? (
                            <GroupSessionSpeakerConfirm />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="group-sessions/edit-session/:group_sessions_series_id/:group_timeslot_id"
                        element={
                          canAccessFeature("group-sessions") ? (
                            <GroupSessionCreateOrEdit />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="group-sessions/edit-series/:group_sessions_series_id"
                        element={
                          canAccessFeature("group-sessions") ? (
                            <GroupSessionCreateOrEdit />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="group-sessions/:group_timeslot_id"
                        element={
                          canAccessFeature("group-sessions") ? (
                            <GroupSession />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="audio-recorder"
                        element={
                          canAccessFeature("audio-recorder") ? (
                            <AudioRecorder key={window.location.href} />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      {canAccessPlatform(user) && (
                        <Route
                          path="messages"
                          element={
                            canAccessFeature("messaging") ? (
                              <Messages />
                            ) : (
                              <PageNotFounded />
                            )
                          }
                        />
                      )}
                      <Route
                        path="messages/:partner_cognito_sub"
                        element={
                          canAccessFeature("messaging") ? (
                            <Messages />
                          ) : (
                            <PageNotFounded />
                          )
                        }
                      />
                      <Route
                        path="notes-and-forms/print/:type"
                        element={<Print />}
                      />
                      <Route
                        path="notes-and-forms/edit-template/:type/:templateId"
                        element={<NewTemplateEditor />}
                      />
                      <Route
                        path="notes-and-forms/edit-template/:type"
                        element={<NewTemplateEditor />}
                      />
                      <Route
                        path="notes-and-forms/note/:note_data_id"
                        element={<NoteFillIn />}
                      />
                      <Route
                        path="notes-and-forms/form/:form_data_id"
                        element={<FormFillIn />}
                      />
                      <Route
                        path="notes-and-forms/view-note-template"
                        element={<NoteFillIn preview={true} />}
                      />
                      <Route
                        path="notes-and-forms/view-form-template"
                        element={<FormFillIn preview={true} />}
                      />
                      <Route
                        path="notes-and-forms/view-form-template/:form_template_id"
                        element={<FormFillIn preview={true} />}
                      />
                      <Route
                        path="patient-charts/:patient_user_id"
                        element={<PatientChart />}
                      />
                      <Route
                        path="patient-charts/:patient_user_id/edit-profile"
                        element={<OverridePatient />}
                      />
                      <Route
                        path="patient-charts/:patient_user_id/contacts"
                        element={<PatientChart />}
                      />
                      <Route
                        path="patient-charts/:patient_user_id/:active_tab"
                        element={<PatientChart />}
                      />

                      <Route
                        path="patient-charts/:patient_user_id/contacts/:contact_user_id"
                        element={<PatientChart />}
                      />
                      <Route path="post-blog" element={<PostBlog />} />
                      <Route
                        path="superbill/view/:unique_id_for_email"
                        element={<SuperbillViewer />}
                      />
                      <Route
                        path="superbill/appointment/:statement_no"
                        element={<SuperbillViewer />}
                      />
                      <Route
                        path="invoice/:receipt_type/:timeslot_id/:patient_id"
                        element={<ReceiptViewer />}
                      />
                      <Route
                        path="invoicing/:type/appointment/:appointment_id"
                        element={<InvoicingEditorPdf />}
                      />
                      {/* TODO: finalize route url */}
                      <Route
                        path="add-client-payment/:patient_user_id"
                        element={<AddClientPayment />}
                      />
                      <Route
                        path="add-client-payment/:patient_user_id/:invoice_no"
                        element={<AddClientPayment />}
                      />
                      <Route
                        path="invoicing-checkout-confirm"
                        element={<InvoicingCheckoutConfirm />}
                      />
                      <Route
                        path="subscription-settings/:suid/update-payment"
                        element={<UpdatePayment />}
                      />
                      <Route
                        path="subscription-settings/:suid/cancel"
                        element={<CancelPage />}
                      />
                      <Route
                        path="subscription-settings/:suid/change-plan"
                        element={<ChangeSubscriptionPlan />}
                      />
                      <Route
                        path="subscription-settings/:suid/change"
                        element={<Subclass />}
                      />
                      <Route
                        path="confirm-all-attendance"
                        element={<ConfirmAllAttendance />}
                      />
                      <Route
                        path="approve-client"
                        element={<ApproveClientSignup />}
                      />
                      <Route
                        path="request-approved"
                        element={<ClientPortalRequestApproved />}
                      />
                      <Route path="mfa-setup" element={<MFA />} />

                      <Route
                        path="*"
                        element={<Navigate to={"homepage"} replace />}
                      />
                    </Routes>
                  </Suspense>
                </div>
              )}
            </main>
          )}
        </div>
      </DndProvider>
    </DoctorDashboardPageContextProvider>
  );
};

export default DoctorDashboard;
